import { createSlice } from "@reduxjs/toolkit";
import { getAllowedManageTeamTabs } from "../../guard/menuUtils";
import { pushDynamicTab } from "../utils/dynamicTabs";
const SLICE_VERSION = "1.0";

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

const allTabs = [
  { id: "teamMembers", name: "Team Members" },
  { id: "org-invites", name: "Organization Invites" },
  { id: "pref-partners", name: "Preferred Partners" },
  { id: "child-org", name: "Office Management" },
  { id: "role-mgmt", name: "Role Management" },
  // Only ever present for accounts holding systemUsers:VIEW/MANAGE (the "Ascend Cargo System
  // Admin" role) — the menu backing "System Users" is a child of manageTeam specifically so
  // getAllowedManageTeamTabs only surfaces it for that privilege, same mechanism as every other
  // tab here.
  { id: "system-users", name: "System Users" },
];

// Get filtered tabs immediately when slice loads
const getInitialTabs = () => {
  const allowedTabs = getAllowedManageTeamTabs("manageTeam");
  return allowedTabs.length > 0
      ? allTabs.filter(tab => allowedTabs.includes(tab.name))
      : allTabs; // Fallback to all tabs if no permissions yet
};

export const initialState = {
  _version: SLICE_VERSION,
  // Filter default tabs by permission when available; fall back to all tabs if nothing is cached yet.
  defaultTabs: getInitialTabs(),
  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],
};

const manageTeamSlice = createSlice({
  name: "manageTeam",
  initialState,
  reducers: {
    addTab: (state, action) => {
      const tab = {
        ...action.payload,
        createdAt: action.payload?.createdAt ?? Date.now(),
      };
      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tab, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },
    removeTab: (state, action) => {
      const { tabIndex, defaultTabsCount } = action.payload;
      const dynamicIndex = tabIndex - defaultTabsCount;
      if (dynamicIndex < 0) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the first default tab.
      state.activeTab = 0;
    },
    setActiveTab: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.defaultTabs.length + state.dynamicTabs.length && index !== state.activeTab) {
        pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
        state.activeTab = index;
      }
    },
    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },
  },
});

export const { addTab, removeTab, setActiveTab, resetTabs } = manageTeamSlice.actions;
export default manageTeamSlice.reducer;
